import { useState, useEffect } from "react";
import { Monitor } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";

const MIN_WIDTH = 1024;

/**
 * MobileOverlay - Displays a full-screen overlay when viewport is < 1024px
 * Blocks access to the app on small screens with a friendly message
 *
 * Uses semantic design tokens for consistent theming:
 * - bg-background for surface color (light/dark aware)
 * - text-foreground for primary text
 * - text-muted-foreground for secondary text
 * - accent colors for decorative elements (safe on any background as icons)
 */
export function MobileOverlay() {
  const { theme } = useTheme();
  const [isTooSmall, setIsTooSmall] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsTooSmall(window.innerWidth < MIN_WIDTH);
    };

    // Check on mount
    checkWidth();

    // Listen for resize
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  if (!isTooSmall) return null;

  const gridImage = theme === "light" ? "/grid_purple.png" : "/grid_white.png";

  return (
    <div
      className={cn(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center p-8",
        "bg-background",
      )}
    >
      {/* Low opacity grid background */}
      <div
        className="absolute inset-0 bg-repeat bg-center opacity-10 pointer-events-none"
        style={{
          backgroundImage: `url(${gridImage})`,
          backgroundSize: "100px",
        }}
      />
      <div className="flex flex-col items-center text-center max-w-[400px]">
        {/* Icon container uses accent color - safe for decorative elements */}
        <div className="w-20 h-20 rounded-[16px] bg-primary/15 border border-primary/40 flex items-center justify-center mb-6">
          <Monitor className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-2xl font-semibold text-foreground mb-3">
          Larger Screen Required
        </h1>
        <p className="text-base leading-relaxed text-muted-foreground text-balance">
          Studio1 is designed for desktop use. Please use a device with a screen
          width of at least 1024 pixels for the best experience.
        </p>
      </div>
    </div>
  );
}
